/**
 * Column Definitions
 *
 * React column renderer definitions for the vulnerabilities table.
 * Contains the cell rendering logic and column configuration.
 *
 * Column widths migrated to central registry (Day 5 - 2025-10-23)
 * Location: src/components/table/columns/registry.ts
 *
 * Migration changes:
 * - Date columns (created/visited) standardized from 180px to 60px
 * - All other columns maintain their original widths
 * - Now uses getColumnWidth('risk', columnId) pattern
 *
 * Rollback instructions (if needed):
 * 1. Revert this file to previous commit
 * 2. Central registry remains (no deletion needed)
 */

import React from 'react';

import { getColumnWidth } from '@/components/table/columns';
import { Column, ColumnPriority } from '@/components/table/types';
import { RiskWithVulnerability } from '@/hooks/useRisks';
import {
  CVSSCell,
  EPSSCell,
  ExploitCell,
  KEVCell,
  OriginCell,
  SeverityCell,
  StatusCell,
  TagsCell,
  TitleCell,
} from '@/sections/vulnerabilities/components/cells';

import { COLUMN_LABELS, COPYABLE_COLUMNS, SORTABLE_COLUMNS } from './columns';

/**
 * Creates default column definitions for the vulnerabilities table
 * @param variant - Table variant ('default' or 'compact'), defaults to 'default'
 * @returns Array of column configurations with cell components
 */
export function createDefaultColumns(
  variant: 'default' | 'compact' = 'default'
): Column<RiskWithVulnerability>[] {
  return [
    {
      id: 'status',
      label: COLUMN_LABELS.status,
      copy: false,
      cell: risk => <StatusCell risk={risk} />,
      fixedWidth: getColumnWidth('risk', 'status', variant).fixedWidth,
      sorting: SORTABLE_COLUMNS.includes('status'),
      responsive: {
        priority: ColumnPriority.CRITICAL,
        alwaysVisible: true,
      },
      // Client-side sorting implemented in useRisks hook
      // Sorts by importance: Opened → Triaged → Remediated → Accepted → Deleted
    },
    {
      id: 'priority',
      label: COLUMN_LABELS.priority,
      fixedWidth: getColumnWidth('risk', 'priority', variant).fixedWidth,
      cell: risk => <SeverityCell risk={risk} />,
      responsive: {
        priority: ColumnPriority.CRITICAL,
        alwaysVisible: true,
      },
    },
    {
      id: 'name',
      label: COLUMN_LABELS.name,
      fixedWidth: getColumnWidth('risk', 'name', variant).fixedWidth,
      minWidth: getColumnWidth('risk', 'name', variant).minWidth,
      maxWidth: getColumnWidth('risk', 'name', variant).maxWidth,
      cell: risk => <TitleCell risk={risk} />,
      responsive: {
        priority: ColumnPriority.CRITICAL,
        alwaysVisible: true,
      },
    },
    {
      id: 'cvss',
      label: COLUMN_LABELS.cvss,
      fixedWidth: getColumnWidth('risk', 'cvss').fixedWidth,
      copy: COPYABLE_COLUMNS.includes('cvss'),
      cell: risk => <CVSSCell risk={risk} />,
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      id: 'epss',
      label: COLUMN_LABELS.epss,
      fixedWidth: getColumnWidth('risk', 'epss').fixedWidth,
      copy: COPYABLE_COLUMNS.includes('epss'),
      cell: risk => <EPSSCell risk={risk} />,
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      id: 'kev',
      label: COLUMN_LABELS.kev,
      fixedWidth: getColumnWidth('risk', 'kev').fixedWidth,
      cell: risk => <KEVCell risk={risk} />,
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      id: 'exploit',
      label: COLUMN_LABELS.exploit,
      fixedWidth: getColumnWidth('risk', 'exploit').fixedWidth,
      cell: risk => <ExploitCell risk={risk} />,
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      id: 'origins', // Maps to risk.origins field (with fallback to risk.source in cell renderer)
      label: COLUMN_LABELS.origin,
      fixedWidth: getColumnWidth('risk', 'origin', variant).fixedWidth,
      copy: false,
      cell: risk => <OriginCell risk={risk} />,
      sorting: false,
      responsive: {
        priority: ColumnPriority.HIGH,
      },
    },
    {
      id: 'tags',
      label: COLUMN_LABELS.tags,
      fixedWidth: getColumnWidth('risk', 'tags').fixedWidth,
      copy: false,
      cell: risk => <TagsCell risk={risk} />,
      sorting: false,
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      id: 'created',
      label: COLUMN_LABELS.created,
      fixedWidth: getColumnWidth('risk', 'created').fixedWidth,
      copy: COPYABLE_COLUMNS.includes('created'),
      cell: 'date',
      responsive: {
        priority: ColumnPriority.MEDIUM,
      },
    },
    {
      id: 'visited',
      label: COLUMN_LABELS.visited,
      fixedWidth: getColumnWidth('risk', 'visited').fixedWidth,
      copy: COPYABLE_COLUMNS.includes('visited'),
      cell: 'date',
      responsive: {
        priority: ColumnPriority.HIGH,
      },
    },
  ];
}

/**
 * Default origin cell renderer (for backward compatibility)
 * Delegates to OriginCell component
 * @deprecated Use OriginCell component directly in column definitions
 */
export function defaultRenderOriginCell(
  risk: RiskWithVulnerability
): React.JSX.Element {
  return <OriginCell risk={risk} />;
}
